import { isShallowEqual } from "@suse-o11y/is-shallow-equal";
import { useHandler } from "@suse-o11y/use-handler";
import { useMemo } from "@suse-o11y/use-memo";
import { z } from "zod";

import { matchQueryKey } from "./match-query-key";
import { useIsFetching, useQueryClient } from "./react-query";

interface UseRefetchQueriesInNamespaceOptions {
	includeInitialFetches?: boolean;
}

export function useRefetchQueriesInNamespace(
	namespace: ReadonlyArray<string>,
	{ includeInitialFetches = false }: UseRefetchQueriesInNamespaceOptions = {},
): [isFetching: boolean, refetch: VoidFunction] {
	const queryClient = useQueryClient();

	const queryMatcher = useMemo(
		() =>
			matchQueryKey({
				namespace: z
					.array(z.string())
					.refine((fragments) => namespace.every((fragment, index) => fragments[index] === fragment)),
			}),
		[namespace],
		[isShallowEqual],
	);

	const fetchingCount = useIsFetching({
		predicate: ({ queryKey, state }) =>
			(includeInitialFetches === true || state.data != null) && state.isFetching && queryMatcher(queryKey),
	});

	const refetch = useHandler(() => {
		void queryClient.invalidateQueries({
			predicate: ({ queryKey }) => queryMatcher(queryKey),
		});
	});

	return [fetchingCount > 0, refetch];
}
